import React, { useCallback, useEffect, useState } from "react";
import { Col, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import * as workflowService from "../../../services/workflowService";
import { skillFor, mirroredStepKey } from "./workflowAssignments";
import "./WorkflowTimeline.css";

// A saved booking's workflow in motion: each step's state, who is on it, and the buttons
// to move it along. Used on the booking's edit screen.
//   - Start / Done / Skip / Reopen / Unlock happen immediately (staff act for people who have
//     no login or do the work themselves); a lost race or a locked step is reported, not hidden.
//   - WHO is on a step is part of the form: choosing a person here is saved with the booking's
//     Update button, like every other field. The service provider step is read-only here: its
//     people are the ones chosen in the crew calendar.

const STATE_LABEL = { PENDING: "Not started", IN_PROGRESS: "In progress", DONE: "Done", SKIPPED: "Skipped" };
const STATE_COLOR = { PENDING: "#6B7280", IN_PROGRESS: "#B45309", DONE: "#2E7D32", SKIPPED: "#6B7280" };

const selectStyle = { height: "34px", fontSize: "13px", borderRadius: "6px", border: "1px solid #E3EBF8" };

const namesOf = (step) => (step.assignees || []).map((a) => a.name || `Person #${a.user_id}`);

function StepRow({ step, crew, busy, disabled, isServiceProvider, mainPeople, chosen, onAct, onChoose, variant, index, isLast, mirroredHint }) {
  const open = step.state === "PENDING" || step.state === "IN_PROGRESS";
  const isResource = step.kind === "RESOURCE";
  const saved = step.assignee_user_id ? Number(step.assignee_user_id) : null;
  const unsaved = !isServiceProvider && chosen !== undefined && (chosen || null) !== saved;

  let who;
  if (isResource) {
    who = <span style={{ fontSize: "13px", color: "#6B7280" }}>Done once a resource is chosen and saved</span>;
  } else if (isServiceProvider) {
    // The people on the crew calendar; for a booking whose calendar slot is gone, the saved people.
    const names = mainPeople.length ? mainPeople : namesOf(step);
    who = names.length ? (
      <span style={{ fontSize: "13px" }}>
        <strong>{names.join(", ")}</strong>
        <span style={{ color: "#6B7280" }}> · {mirroredHint.toLowerCase()}</span>
      </span>
    ) : (
      <span style={{ fontSize: "13px", color: "#6B7280" }}>{mirroredHint}</span>
    );
  } else if (open && !disabled) {
    const value = chosen === undefined ? saved || "" : chosen || "";
    who = (
      <Form.Select
        size="sm"
        style={selectStyle}
        aria-label={`${step.name} person`}
        value={value}
        onChange={(e) => onChoose(step, e.target.value)}
      >
        <option value="">Not assigned</option>
        {value && !crew.some((c) => c.user_id === Number(value)) && (
          <option value={value}>{Number(value) === saved && step.assignee_name ? step.assignee_name : `Person #${value}`}</option>
        )}
        {crew.map((c) => (
          <option key={c.user_id} value={c.user_id}>
            {c.name}
            {c.busy ? ` (busy: ${c.busy_with || "another booking"})` : ""}
          </option>
        ))}
      </Form.Select>
    );
  } else {
    who = <span style={{ fontSize: "13px" }}>{step.assignee_name || (saved ? `Person #${saved}` : "Not assigned")}</span>;
  }

  const timeline = variant === "timeline";

  const inner = (
    <div className="d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
        <div style={{ flex: "1 1 190px", minWidth: "170px" }}>
          <span style={{ fontWeight: 600, fontSize: "14px" }}>{step.name}</span>
          {isServiceProvider && (
            <span style={{ fontSize: "11px", color: "#C2185B", marginLeft: "8px" }} title="Completes the service">
              ★ Service provider
            </span>
          )}
          {!step.is_mandatory && <span style={{ fontSize: "11px", color: "#6B7280", marginLeft: "8px" }}>Optional</span>}
          <div style={{ fontSize: "12px", color: STATE_COLOR[step.state] }}>
            {STATE_LABEL[step.state]}
            {step.locked && <span style={{ color: "#B45309" }}> · waiting for "{step.blocked_by}"</span>}
          </div>
        </div>

        <div style={{ flex: "1 1 200px", minWidth: "180px" }}>
          {who}
          {unsaved && <div style={{ fontSize: "11px", color: "#B45309" }}>Saved when you press Update</div>}
        </div>

        {!isResource && !disabled && (
          <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
            {step.state === "PENDING" && (
              <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => onAct(step, "START")}>
                Start
              </button>
            )}
            {open && (
              <button type="button" className="btn btn-sm text-white" style={{ background: "#C2185B" }} disabled={busy} onClick={() => onAct(step, "DONE")}>
                Done
              </button>
            )}
            {open && (
              <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => onAct(step, "SKIP")}>
                Skip
              </button>
            )}
            {step.locked && (
              <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => onAct(step, "UNLOCK")}>
                Unlock
              </button>
            )}
            {!open && (
              <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => onAct(step, "REOPEN")}>
                Reopen
              </button>
            )}
          </div>
        )}
    </div>
  );

  // Not a nested component: one defined inside this function would be a new type on every
  // render and remount the person select while it is being used.
  if (timeline) {
    return (
      <div className="wf-tl-item" data-testid={`run-step-${step.step_key}`}>
        <div className="wf-tl-rail" aria-hidden="true">
          <span className="wf-tl-dot">{index + 1}</span>
          {!isLast && <span className="wf-tl-line" />}
        </div>
        <div className="wf-tl-body">{inner}</div>
      </div>
    );
  }
  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid #F1F3F9" }} data-testid={`run-step-${step.step_key}`}>
      {inner}
    </div>
  );
}

// props
//   workflow       from GET booking_workflow (name, steps[] with state / locked / assignees)
//   onChanged(wf)  called with the fresh workflow after every Start / Done / ... change
//   onStatusChanged(statusId)  the booking's status was moved by the workflow
//   disabled       the booking can't be edited (read only)
//   assignments { step_key: userId }   who the form currently has on each step (saved with Update)
//   onAssign(stepKey, userId | null)   the user chose a person
//   mainPeople [names]                 who the crew calendar has chosen (the service provider step)
//   date / time / durationMin / excludeGroupId   what "busy" is judged against
//   variant "timeline" draws the steps on a vertical timeline (default: the plain list)
//   mirroredHint   what the service provider step says (default: chosen in the calendar above)
function WorkflowRunPanel({
  workflow,
  onChanged,
  onStatusChanged,
  disabled,
  assignments,
  onAssign,
  mainPeople = [],
  date,
  time,
  durationMin,
  excludeGroupId,
  variant,
  mirroredHint = "Chosen in the calendar above",
}) {
  const [crew, setCrew] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const steps = workflow?.steps || [];
  const serviceProviderKey = mirroredStepKey(steps);
  const skillKeys = [
    ...new Set(steps.filter((s) => s.kind === "CREW" && s.step_key !== serviceProviderKey).map(skillFor)),
  ].join(",");

  useEffect(() => {
    if (!skillKeys || disabled) return undefined;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        skillKeys.split(",").map(async (skill) => {
          try {
            const res = await workflowService.getEligibleCrew({ skill, date, time, durationMin, excludeGroupId });
            return [skill, res?.success ? res.data.crew || [] : []];
          } catch (e) {
            return [skill, []];
          }
        })
      );
      if (!cancelled) setCrew(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [skillKeys, disabled, date, time, durationMin, excludeGroupId]);

  // Refreshes from the server after anything that was refused, so the screen shows what is true.
  const refresh = useCallback(async () => {
    try {
      const res = await workflowService.getBookingWorkflow(workflow.group_id);
      if (res?.success && res.data) onChanged(res.data);
    } catch (e) {
      // keep what is on screen
    }
  }, [workflow?.group_id, onChanged]);

  const handle = async (step, run, okMessage) => {
    setBusyKey(step.step_key);
    try {
      const res = await run();
      if (res?.success) {
        if (res.data?.workflow) onChanged(res.data.workflow);
        if (res.data?.status_changed) {
          toast.success(`Booking moved to ${res.data.status_changed}`);
          if (res.data.status_id && onStatusChanged) onStatusChanged(res.data.status_id);
        } else if (okMessage) {
          toast.success(okMessage);
        }
        if (res.data?.status_warning) toast.warn(res.data.status_warning);
      } else {
        toast.error(res?.message || "Something went wrong");
        await refresh();
      }
    } catch (e) {
      toast.error("Something went wrong");
    }
    setBusyKey("");
  };

  const act = (step, action) => {
    let override = false;
    if (action === "SKIP" && step.is_mandatory) {
      if (!window.confirm(`"${step.name}" is a required step. Skip it anyway?`)) return;
      override = true;
    }
    return handle(step, () => workflowService.changeStepState({ groupId: workflow.group_id, stepKey: step.step_key, action, override }));
  };

  const choose = (step, value) => {
    if (onAssign) onAssign(step.step_key, value ? Number(value) : null);
  };

  if (!workflow || !steps.length) return null;
  return (
    <Col xs={12} style={{ marginBottom: "1%" }}>
      <div className="rounded border p-3" style={{ borderColor: "#E3EBF8" }} data-testid="workflow-run-panel">
        <div style={{ fontWeight: 600, fontSize: "15px" }}>{workflow.name}</div>
        <div style={{ fontSize: "12px", color: "#6B7280" }}>
          Start, Done and Skip are saved straight away. People are saved when you press Update.
        </div>
        {steps.map((s, i) => (
          <StepRow
            key={s.step_key}
            variant={variant}
            index={i}
            isLast={i === steps.length - 1}
            mirroredHint={mirroredHint}
            step={s}
            crew={crew[skillFor(s)] || []}
            busy={busyKey === s.step_key}
            disabled={disabled}
            isServiceProvider={s.step_key === serviceProviderKey}
            mainPeople={mainPeople}
            chosen={assignments ? assignments[s.step_key] ?? null : undefined}
            onAct={act}
            onChoose={choose}
          />
        ))}
      </div>
    </Col>
  );
}

export default WorkflowRunPanel;
